import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useBoard } from '../hooks/useBoard';
import { BoardDisplay } from '../components/BoardDisplay';
import { BoardLaunchLoader } from '../components/BoardLaunchLoader';
import { AuthStatus } from '../components/AuthStatus';
import { Logo } from '../components/Logo';
import { ThemeToggle } from '../components/ThemeToggle';
import { useAuth } from '../contexts/AuthContext';
import {
  AdminPasswordModal,
  BoardPasswordModal,
  UserNameModal,
  BoardSettingsModal,
  AddColumnModal,
  EditColumnModal,
  EditCardModal,
  DeleteColumnModal,
} from '../components/modals';
import type { Board, BoardParticipant, Card, Column, BoardSettingsUpdate } from '../types/board';

const USER_ID_KEY = 'retrojet-user-id';
const USER_NAME_KEY = 'retrojet-user-name';
type CardSortOption = 'manual' | 'time' | 'user';

interface Participant {
  id: string;
  name: string;
  isAdmin: boolean;
}

const PARTICIPANT_COLORS = [
  '#EF4444',
  '#F59E0B',
  '#10B981',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
];

function getDisplayUserName(userName: string): string {
  return userName === 'Anonymous' ? 'Anon.' : userName;
}

function sortCards(cards: Card[], sortOption: CardSortOption): Card[] {
  if (sortOption === 'manual') {
    return cards;
  }

  const sortedCards = [...cards];
  if (sortOption === 'time') {
    sortedCards.sort((first, second) => (
      new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime()
    ));
  } else {
    sortedCards.sort((first, second) => (
      first.author.localeCompare(second.author, undefined, { sensitivity: 'base' })
    ));
  }
  return sortedCards;
}

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0][0]?.toUpperCase() ?? '?';
  return `${words[0][0] ?? ''}${words[words.length - 1][0] ?? ''}`.toUpperCase();
}

function getUserColor(identifier: string, index: number): string {
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
  }
  return PARTICIPANT_COLORS[Math.abs(hash) % PARTICIPANT_COLORS.length]
    ?? PARTICIPANT_COLORS[index % PARTICIPANT_COLORS.length]
    ?? '#3B82F6';
}

function getParticipants(
  activeParticipants: BoardParticipant[],
  userName: string,
  userId: string,
  isCurrentUserAdmin: boolean
): Participant[] {
  const participantsById = new Map<string, Participant>();

  activeParticipants.forEach((participant) => {
    participantsById.set(participant.id, {
      id: participant.id,
      name: participant.name,
      isAdmin: participant.isAdmin,
    });
  });

  participantsById.set(userId, {
    id: userId,
    name: getDisplayUserName(userName),
    isAdmin: isCurrentUserAdmin,
  });

  return Array.from(participantsById.values()).sort((first, second) => {
    if (first.isAdmin && !second.isAdmin) return -1;
    if (!first.isAdmin && second.isAdmin) return 1;
    return first.name.localeCompare(second.name);
  });
}

function getOrCreateUserId(): string {
  let userId = localStorage.getItem(USER_ID_KEY);
  if (!userId) {
    userId = crypto.randomUUID();
    localStorage.setItem(USER_ID_KEY, userId);
  }
  return userId;
}

function getInitialBoardUserName(authUserName?: string): string {
  const storedUserName = localStorage.getItem(USER_NAME_KEY);
  if (storedUserName && storedUserName.trim().length > 0 && storedUserName !== 'Anonymous') {
    return storedUserName;
  }
  return authUserName ?? 'Anonymous';
}

function BoardTopBar(): JSX.Element {
  return (
    <header className="border-b border-gray-200 bg-white px-4 py-4 shadow-sm dark:border-gray-800 dark:bg-gray-950 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <Link
            to="/"
            className="btn-secondary text-sm"
            aria-label="Go back to home"
          >
            <span aria-hidden="true">&larr; </span>
            <span className="sm:hidden">Home</span>
            <span className="hidden sm:inline">Back to Home</span>
          </Link>
          <Link to="/" className="flex items-center">
            <Logo size="md" className="justify-start" logoWidth="w-5" />
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <div data-testid="theme-toggle">
            <ThemeToggle />
          </div>
          <AuthStatus
            compact={false}
            signInAlign="end"
          />
        </div>
      </div>
    </header>
  );
}

export function BoardPage(): JSX.Element {
  const { boardId } = useParams<{ boardId: string }>();
  const { user } = useAuth();
  const [guestUserId] = useState(getOrCreateUserId);
  const [boardUserName, setBoardUserName] = useState(() => getInitialBoardUserName(user?.name));
  const [cardSortOption, setCardSortOption] = useState<CardSortOption>('manual');
  const userId = user?.id ?? guestUserId;
  const userName = boardUserName;

  // Modal states
  const [showSettings, setShowSettings] = useState(false);
  const [showAdminPasswordModal, setShowAdminPasswordModal] = useState(false);
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [editingCard, setEditingCard] = useState<Card | null>(null);
  const [editingColumn, setEditingColumn] = useState<Column | null>(null);
  const [deletingColumn, setDeletingColumn] = useState<Column | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [isUpdatingCard, setIsUpdatingCard] = useState(false);
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
  const [isPromotingToAdmin, setIsPromotingToAdmin] = useState(false);
  const [promoteAdminError, setPromoteAdminError] = useState<string | null>(null);
  const [showEditUserName, setShowEditUserName] = useState(false);
  const [isUpdatingUserName, setIsUpdatingUserName] = useState(false);
  const [editUserNameError, setEditUserNameError] = useState<string | null>(null);
  const [adminRevealCardsForMe, setAdminRevealCardsForMe] = useState(false);

  const {
    board,
    isLoading,
    error,
    isConnected,
    reconnectAttempts,
    isAdmin,
    participants: activeParticipants,
    requiresPassword,
    requiresUserName,
    hasJoined,
    joinError,
    revealHiddenCards,
    hideHiddenCards,
    addCard,
    moveCard,
    updateCard,
    deleteCard,
    voteCard,
    removeCardVote,
    mergeCards,
    addColumn,
    updateColumn,
    deleteColumn,
    reorderColumns,
    updateBoardSettings,
    promoteToAdmin,
    joinBoard,
    updateUserBoardName,
  } = useBoard({ boardId: boardId || '', userId, userName });

  useEffect(() => {
    if (!user) return;

    const storedUserName = localStorage.getItem(USER_NAME_KEY);
    if (!storedUserName || storedUserName === 'Anonymous') {
      setBoardUserName(user.name);
      localStorage.setItem(USER_NAME_KEY, user.name);
    }
  }, [user]);

  useEffect(() => {
    if (!isAdmin || board?.settings.cardVisibility !== 'BLURRED') {
      setAdminRevealCardsForMe(false);
    }
  }, [board?.settings.cardVisibility, isAdmin]);

  const handleToggleAdminRevealCardsForMe = useCallback(async (): Promise<void> => {
    if (adminRevealCardsForMe) {
      setAdminRevealCardsForMe(false);
      await hideHiddenCards();
      return;
    }

    await revealHiddenCards();
    setAdminRevealCardsForMe(true);
  }, [adminRevealCardsForMe, hideHiddenCards, revealHiddenCards]);

  // Password modal handler
  const handlePasswordSubmit = useCallback(async (password: string) => {
    setIsJoining(true);
    try {
      await joinBoard(password, userName !== 'Anonymous' ? userName : undefined);
    } finally {
      setIsJoining(false);
    }
  }, [joinBoard, userName]);

  // Username modal handler
  const handleUserNameSubmit = useCallback(async (name: string) => {
    setIsJoining(true);
    try {
      await joinBoard(undefined, name);
      setBoardUserName(name);
      localStorage.setItem(USER_NAME_KEY, name);
    } finally {
      setIsJoining(false);
    }
  }, [joinBoard]);

  const handleEditUserNameSubmit = useCallback(async (name: string) => {
    setIsUpdatingUserName(true);
    setEditUserNameError(null);
    try {
      const savedUserName = await updateUserBoardName(name);
      setBoardUserName(savedUserName);
      localStorage.setItem(USER_NAME_KEY, savedUserName);
      setShowEditUserName(false);
    } catch (err) {
      setEditUserNameError(err instanceof Error ? err.message : 'Failed to update name');
    } finally {
      setIsUpdatingUserName(false);
    }
  }, [updateUserBoardName]);

  // Settings handlers
  const handleSaveSettings = useCallback(async (settings: BoardSettingsUpdate) => {
    setIsUpdatingSettings(true);
    try {
      await updateBoardSettings(settings);
      setShowSettings(false);
    } finally {
      setIsUpdatingSettings(false);
    }
  }, [updateBoardSettings]);

  const handlePromoteToAdmin = useCallback(async (adminPassword: string) => {
    setIsPromotingToAdmin(true);
    setPromoteAdminError(null);
    try {
      await promoteToAdmin(adminPassword);
      setShowAdminPasswordModal(false);
    } catch (err) {
      setPromoteAdminError(err instanceof Error ? err.message : 'Failed to become admin');
    } finally {
      setIsPromotingToAdmin(false);
    }
  }, [promoteToAdmin]);

  // Column handlers
  const handleAddColumn = useCallback(async (name: string, color: string) => {
    await addColumn(name, color);
    setShowAddColumn(false);
  }, [addColumn]);

  const handleEditColumn = useCallback(async (name: string, color: string) => {
    if (editingColumn) {
      await updateColumn(editingColumn.id, name, color);
      setEditingColumn(null);
    }
  }, [editingColumn, updateColumn]);

  const handleDeleteColumn = useCallback(async (migrateToColumnId?: string) => {
    if (deletingColumn) {
      await deleteColumn(deletingColumn.id, migrateToColumnId);
      setDeletingColumn(null);
    }
  }, [deletingColumn, deleteColumn]);

  const handleEditCard = useCallback(async (content: string): Promise<void> => {
    if (!editingCard) return;
    setIsUpdatingCard(true);
    try {
      await updateCard(editingCard.id, content);
      setEditingCard(null);
    } finally {
      setIsUpdatingCard(false);
    }
  }, [editingCard, updateCard]);

  const handleSetBoardVisibility = useCallback(async (cardVisibility: 'VISIBLE' | 'BLURRED'): Promise<void> => {
    if (!board) return;
    if (cardVisibility === 'BLURRED') {
      setAdminRevealCardsForMe(false);
    }
    await updateBoardSettings({
      cardVisibility,
    });
  }, [board, updateBoardSettings]);

  const handleSetVoteVisibility = useCallback(async (showVotesToParticipants: boolean): Promise<void> => {
    if (!board) return;
    await updateBoardSettings({
      showVotesToParticipants,
    });
  }, [board, updateBoardSettings]);

  // Loading state
  if (!boardId) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <p className="text-red-600 dark:text-red-400">Invalid board ID</p>
      </div>
    );
  }

  if (isLoading) {
    return <BoardLaunchLoader />;
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
          <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">Error</h2>
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-600 dark:text-gray-400">Board not found</p>
      </div>
    );
  }

  // Show password modal
  if (requiresPassword && !hasJoined) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <BoardPasswordModal
          isOpen={true}
          onClose={() => {}}
          onSubmit={handlePasswordSubmit}
          boardName={board.name}
          error={joinError}
          isLoading={isJoining}
        />
      </div>
    );
  }

  // Show username modal
  if (requiresUserName && !hasJoined) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <UserNameModal
          isOpen={true}
          onClose={() => {}}
          onSubmit={handleUserNameSubmit}
          initialName={userName !== 'Anonymous' ? userName : ''}
          error={joinError}
          isLoading={isJoining}
        />
      </div>
    );
  }

  const otherColumns = board.columns.filter((c) => c.id !== deletingColumn?.id);
  const participants = getParticipants(activeParticipants, userName, userId, isAdmin);
  const displayUserName = getDisplayUserName(userName);
  const displayBoard: Board = {
    ...board,
    columns: board.columns.map((column) => ({
      ...column,
      cards: sortCards(column.cards, cardSortOption),
    })),
  };
  const isBoardBlurred = board.settings.cardVisibility === 'BLURRED';
  const canBecomeAdmin = !isAdmin && board.hasAdminPassword;

  return (
    <div
      className="flex min-h-0 flex-1 flex-col bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-white"
      data-testid="board-page"
    >
      <BoardTopBar />

      {!isConnected && (
        <div className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-center dark:border-amber-800 dark:bg-amber-950/40">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
            {reconnectAttempts > 0
              ? `Reconnecting... (attempt ${reconnectAttempts}/5)`
              : 'Disconnected from server'}
          </p>
        </div>
      )}

      <section className="border-b border-gray-200 bg-gray-100 px-6 py-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <h1 className="break-words text-3xl font-bold tracking-normal text-gray-900 dark:text-white">
              {board.name}
            </h1>
            <p className="mt-2 text-base font-medium text-gray-600 dark:text-gray-300">
              {isAdmin ? 'You are the admin' : 'You are a participant'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => {
                setEditUserNameError(null);
                setShowEditUserName(true);
              }}
              className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-blue-400 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-500 dark:hover:text-blue-300"
              aria-label="Edit your name"
              title="Edit your name"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <span>{displayUserName}</span>
              <svg
                className="h-3.5 w-3.5 opacity-70"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>

            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                Participants:
              </span>
              <div className="flex items-center gap-1">
                {participants.map((participant, index) => (
                  <div
                    key={participant.id}
                    className="relative"
                    data-testid="participant-avatar"
                    data-participant-id={participant.id}
                    data-participant-name={participant.name}
                    title={`${participant.name}${participant.isAdmin ? ' (Admin)' : ''}`}
                  >
                    <div
                      className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-white text-xs font-bold text-white shadow-sm dark:border-gray-900"
                      style={{ backgroundColor: getUserColor(participant.id, index) }}
                    >
                      {getInitials(participant.name)}
                    </div>
                    {participant.isAdmin && (
                      <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-yellow-400 dark:border-gray-900" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      </section>

      <section className="flex flex-col gap-4 border-b border-gray-200 bg-gray-100 px-6 py-4 dark:border-gray-800 dark:bg-gray-900 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="card-sort" className="text-sm font-semibold text-gray-600 dark:text-gray-300">
            Sort cards:
          </label>
          <select
            id="card-sort"
            value={cardSortOption}
            onChange={(event) => setCardSortOption(event.target.value as CardSortOption)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          >
            <option value="manual">Manual (drag to sort)</option>
            <option value="time">By Time (newest first)</option>
            <option value="user">By User (A-Z)</option>
          </select>
        </div>

        {(isAdmin || canBecomeAdmin) && (
          <div className="flex flex-wrap items-center gap-2">
            {canBecomeAdmin && (
              <button
                type="button"
                onClick={() => {
                  setPromoteAdminError(null);
                  setShowAdminPasswordModal(true);
                }}
                className="rounded-lg bg-purple-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-purple-700"
              >
                Become Admin
              </button>
            )}
            {isAdmin && (
              <>
                {isBoardBlurred && (
                  <button
                    type="button"
                    onClick={() => void handleToggleAdminRevealCardsForMe()}
                    className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
                  >
                    {adminRevealCardsForMe ? 'Blur for me' : 'Unblur for me'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void handleSetBoardVisibility(isBoardBlurred ? 'VISIBLE' : 'BLURRED')}
                  className={`rounded-lg px-3 py-2 text-xs font-semibold text-white shadow-sm transition ${
                    isBoardBlurred
                      ? 'bg-orange-600 hover:bg-orange-700'
                      : 'bg-gray-600 hover:bg-gray-700'
                  }`}
                >
                  {isBoardBlurred ? 'Unblur for everyone' : 'Blur for everyone'}
                </button>
                {board.settings.votingEnabled && (
                  <button
                    type="button"
                    onClick={() => void handleSetVoteVisibility(!board.settings.showVotesToParticipants)}
                    className={`rounded-lg px-3 py-2 text-xs font-semibold text-white shadow-sm transition ${
                      board.settings.showVotesToParticipants
                        ? 'bg-gray-600 hover:bg-gray-700'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {board.settings.showVotesToParticipants
                      ? 'Hide votes from participants'
                      : 'Show votes to participants'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowAddColumn(true)}
                  className="rounded-lg bg-green-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-green-700"
                >
                  Add Column
                </button>
                <button
                  type="button"
                  onClick={() => setShowSettings(true)}
                  className="rounded-lg bg-gray-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-gray-700"
                >
                  Board Settings
                </button>
              </>
            )}
          </div>
        )}
      </section>

      <div className="min-h-0 flex-1 overflow-auto bg-gray-50 dark:bg-gray-950">
        <BoardDisplay
          board={displayBoard}
          userName={userName}
          userId={userId}
          onAddCard={addCard}
          onMoveCard={moveCard}
          onDeleteCard={deleteCard}
          onEditCard={setEditingCard}
          onMergeCards={mergeCards}
          onVoteCard={voteCard}
          onRemoveCardVote={removeCardVote}
          onReorderColumns={reorderColumns}
          onEditColumn={setEditingColumn}
          onDeleteColumn={setDeletingColumn}
          enableMergeCards={board.settings?.enableMergeCards || false}
          isAdmin={isAdmin}
          revealAllCards={isAdmin && adminRevealCardsForMe}
        />
      </div>

      {/* Modals */}
      <BoardSettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onSave={handleSaveSettings}
        settings={board.settings}
        isLoading={isUpdatingSettings}
      />

      <AdminPasswordModal
        isOpen={showAdminPasswordModal}
        onClose={() => {
          setShowAdminPasswordModal(false);
          setPromoteAdminError(null);
        }}
        onSubmit={handlePromoteToAdmin}
        error={promoteAdminError}
        isLoading={isPromotingToAdmin}
      />

      <AddColumnModal
        isOpen={showAddColumn}
        onClose={() => setShowAddColumn(false)}
        onAdd={handleAddColumn}
      />

      <EditColumnModal
        isOpen={editingColumn !== null}
        onClose={() => setEditingColumn(null)}
        onSave={handleEditColumn}
        column={editingColumn}
      />

      <EditCardModal
        isOpen={editingCard !== null}
        onClose={() => setEditingCard(null)}
        onSave={handleEditCard}
        card={editingCard}
        isLoading={isUpdatingCard}
      />

      <DeleteColumnModal
        isOpen={deletingColumn !== null}
        onClose={() => setDeletingColumn(null)}
        onDelete={handleDeleteColumn}
        column={deletingColumn}
        otherColumns={otherColumns}
      />

      <UserNameModal
        isOpen={showEditUserName}
        onClose={() => {
          setShowEditUserName(false);
          setEditUserNameError(null);
        }}
        onSubmit={handleEditUserNameSubmit}
        initialName={userName !== 'Anonymous' ? userName : ''}
        isLoading={isUpdatingUserName}
        title="Edit Your Name"
        description="Choose the name shown on this board."
        submitLabel="Save Name"
        loadingLabel="Saving..."
        error={editUserNameError}
        inputTestId="edit-user-name-input"
        submitTestId="save-user-name-submit"
      />
    </div>
  );
}
